import client from "./client"

const ERROR_TYPES = {
  NONE: "None",
  INVALID_USER: "InvalidUser",
  INVALID_PASSWORD: "InvalidPassword",
  INVALID_USER_TYPE: "InvalidUserType",
  INVALID_TOKEN: "InvalidToken",
  EXPIRED_TOKEN: "ExpiredToken",
  EMPTY_ANSWERS: "AnswersEmpty",
  NO_LICENSE: "NOLICENSE",
  SERVER_ERROR: "ServerError",
  ACCOUNT_LINKED: "AccountCurrentlyLinked",
  EXPIRED_ACTIVITY: "ExpiredContent",
  INVALID_USER_SIGNING_IN_WITH_GOOGLE: "InvalidUserSigningInWithGoogleInstead",
  INVALID_USER_SIGNING_IN_WITH_APPLE: "InvalidUserSigningInWithAppleInstead",
  INVALID_USER_SIGNING_IN_WITH_MICROSOFT:
    "InvalidUserSigningInWithMicrosoftInstead",
  INVALID_PICTOGRAMS: "InvalidPictograms",
}

const AXIOS_CODE_TYPES = {
  NO_INTERNET_CONNECTION: "ENETWORKCHANGED",
  CONNECTION_ABORTED: "ECONNABORTED",
  TIME_OUT: "ETIMEDOUT",
}

const formatResponse = ({
  success,
  message,
  data,
  duration,
  aborted = false,
  error = "",
}) => {
  return {
    success,
    message,
    data,
    duration: duration || 0,
    aborted,
    error,
  }
}
export const sendRequest = async ({
  endpoint,
  body,
  method,
  controller,
  responseType,
  otherOptions,
  context = "",
}) => {
  try {
    const METHOD = method.toLowerCase()
    const cleanEndpoint = endpoint.replace(/^\//, "")
    const finalEndpoint = `${context}/${cleanEndpoint}`
    const response = await client.request({
      method: METHOD,
      url: finalEndpoint,
      ...(METHOD !== "get" && { data: body }),
      ...(METHOD === "get" && controller && { signal: controller.signal }),
      ...(responseType && { responseType: responseType }),
      ...otherOptions,
    })
    if (METHOD === "post") {
      return formatResponse({
        success:
          !response?.data?.error ||
          response?.data?.error === "" ||
          response?.data?.error === "None",
        systemErrorMessage: response?.data?.errorMessage,
        message:
          response?.data?.errorMessage?.length > 0 ? "unexpected error" : "",
        data: response.data,
        error: response?.data?.error || "",
      })
    } else {
      return formatResponse({
        success: true,
        message: "",
        data: response.data,
      })
    }
  } catch (error) {
    if (controller?.signal?.aborted)
      return formatResponse({
        success: false,
        message: "Request aborted [REQUEST_ABORTED]",
        duration: 0,
        aborted: true,
        error: "[REQUEST_ABORTED]",
      })
    const { code, response, request } = error
    if (
      code === AXIOS_CODE_TYPES.CONNECTION_ABORTED ||
      code === AXIOS_CODE_TYPES.TIME_OUT
    ) {
      return formatResponse({
        success: false,
        message: "Please check your internet connection",
        duration: 0,
        errorCode: "[CONNECTION_ABORTED OR TIME_OUT]",
      })
    }
    if (response) {
      console.log(
        "The server responded with a status code outside the 2xx range"
      )
      let errorMessage =
        response?.data?.errorMessage?.length > 0 ? "unexpected error" : ""
      switch (response?.data?.error || response?.data?.errorCode) {
        case ERROR_TYPES.NONE:
          break
        case ERROR_TYPES.INVALID_USER:
        case ERROR_TYPES.INVALID_PASSWORD:
          errorMessage = "Invalid username or password"
          break
        case ERROR_TYPES.INVALID_USER_TYPE:
          break
        case ERROR_TYPES.INVALID_TOKEN:
        case ERROR_TYPES.EXPIRED_TOKEN:
          // should logout user
          errorMessage = "Please, login again"
          break
        case ERROR_TYPES.EMPTY_ANSWERS:
          errorMessage = "Invalid answers, answers cannot be null or empty"
          break
        case ERROR_TYPES.NO_LICENSE:
          errorMessage = "Suspended license. Login to login.smasheducation.com"
          break
        case ERROR_TYPES.ACCOUNT_LINKED:
          errorMessage = "Account currently linked"
          break
        case ERROR_TYPES.EXPIRED_ACTIVITY:
          errorMessage = "ModuleStatusMessageExpired"
          break
        case ERROR_TYPES.INVALID_USER_SIGNING_IN_WITH_GOOGLE:
          errorMessage = "This account uses Google. Please sign in with Google."
          break
        case ERROR_TYPES.INVALID_USER_SIGNING_IN_WITH_APPLE:
          errorMessage = "This account uses Apple. Please sign in with Apple."
          break
        case ERROR_TYPES.INVALID_USER_SIGNING_IN_WITH_MICROSOFT:
          errorMessage =
            "This account uses Microsoft. Please sign in with Microsoft."
          break
        case ERROR_TYPES.INVALID_PICTOGRAMS:
          errorMessage = "Invalid pictogram selection. Please try again."
          break
      }
      if (errorMessage === "" || errorMessage === "unexpected error") {
        const technicalDetails = `${
          response?.data?.error ||
          response?.data?.errorCode ||
          "[STATUS_CODE_" + response.status + "]"
        } | ${response.config.url}`
        console.error("API request failed:", technicalDetails)
        errorMessage = "An unexpected error occurred."
      }
      return formatResponse({
        success: false,
        message: errorMessage,
        error: response?.data?.error || "",
        errorCode:
          response?.data?.error ||
          response?.data?.errorCode ||
          "[STATUS_CODE_" + response.status + "]",
        duration: 0,
      })
    }
    if (request) {
      console.log("The request was made but no response was received")
      return formatResponse({
        success: false,
        message:
          "The request was made but no response was received [NO_RESPONSE_RECEIVED]",
        duration: 0,
        errorCode: "[NO_RESPONSE_RECEIVED]",
      })
    }
    console.log(
      "Something happened in setting up the request that triggered an error"
    )
    return formatResponse({
      success: false,
      message: "unexpected error [REQUEST_SETUP_ERROR]",
      duration: 0,
      errorCode: "[REQUEST_SETUP_ERROR]",
    })
  }
}

export const sendFormData = async ({
  endpoint,
  formData,
  onUploadProgress,
  headers,
  context = "student",
}) => {
  try {
    const cleanEndpoint = endpoint.replace(/^\//, "")
    const finalEndpoint = `${context}/${cleanEndpoint}`
    const response = await client.post(finalEndpoint, formData, {
      headers: { "Content-Type": "multipart/form-data", ...headers },
      // timeout: 60000, // 1min
      ...(onUploadProgress && { onUploadProgress }),
    })
    return formatResponse({
      success:
        response?.data?.error === "" ||
        response?.data?.error === "None" ||
        response?.data?.status,
      systemErrorMessage: response?.data?.errorMessage,
      message:
        response?.data?.errorMessage?.length > 0 ? "unexpected error" : "",
      data: response.data,
    })
  } catch (error) {
    return formatResponse({
      success: false,
      systemErrorMessage: error?.response
        ? error.response.data.errorMessage
        : "unexpected error",
      message: "unexpected error",
      error: error?.response?.data?.error || "",
      data: error?.response?.data,
    })
  }
}

const createRequest = (context = "") => ({
  sendRequest: (options) => sendRequest({ ...options, context }),
  sendFormData: (options) => sendFormData({ ...options, context }),
})

export const studentRequest = createRequest("u/student")
export const professorRequest = createRequest("u/professor")
export const coreRequest = createRequest("u/core")
export const administrativeRequest = createRequest("u/administrative")
